import { useMemo, useRef } from 'react';

import { useParams } from 'react-router';
import { useMount } from 'react-use';

import { Button, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import LinkIcon from '@/components/Icons/LinkIcon';
import Loading from '@/components/Loading';
import { links } from '@/localization';
import { WorkspaceChainFragment } from '@/sections/Overview/Chains/__generated__/fragments.generated';
import { useChains } from '@/sections/Overview/Chains/hooks';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';

import { BASE_FUSE_SEARCH_OPTIONS, mapFilterToTitle, mapSortByToTitle } from '../constants';
import {
  useFilterQuery,
  useFilterSortBy,
  useFilterStatus,
  useTableContainerHeight,
  useViewMode,
} from '../hooks';
import EmptyState from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { Filter, MatchParams, SortBy, WorkspaceSections } from '../types';
import { filterChainsByStatus } from '../utils';
import ChainsEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { getChainsWithDeployments, sortChains } from './utils';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function Chains() {
  const { teamId } = useParams<MatchParams>();
  const { chains, loading, refetch } = useChains(true, teamId);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.Chains);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Chains);
  const { filterStatus, setFilterStatus } = useFilterStatus(WorkspaceSections.Chains);
  const { filterSortBy, setFilterSortBy } = useFilterSortBy(WorkspaceSections.Chains);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = useTableContainerHeight(containerRef, Number(loading));

  useMount(() => {
    refetch();
  });

  const sortedChains = useMemo(() => {
    const chainsWithDeployments = getChainsWithDeployments(chains);

    return sortChains(chainsWithDeployments, filterSortBy as SortBy);
  }, [chains, filterSortBy]);

  const filteredChains = useMemo(() => {
    const filteredByStatus = filterChainsByStatus(sortedChains, filterStatus);

    if (!filterQuery) {
      return filteredByStatus;
    }

    const fuse = new Fuse<WorkspaceChainFragment>(filteredByStatus, FUSE_SEARCH_OPTIONS);
    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, sortedChains, filterStatus]);

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(chains)) {
      return <ChainsEmptyState />;
    }

    if (isEmpty(filteredChains)) {
      return <EmptyState>No Chains found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid chains={filteredChains} />
    ) : (
      <Table chains={filteredChains} height={tableContainerHeight} />
    );
  }

  return (
    <Stack height="100%">
      <Stack gap={3} px={4} pt={4} pb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer>
              <LinkIcon />
            </IconContainer>
            <Typography variant="h2">Chains</Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ExternalLinkIcon />}
            href={links.basetenDocsChainsOverview}
            target="_blank"
          >
            Deploy a Chain
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Search query={filterQuery} setQuery={setFilterQuery} />
          <ListOptions<SortBy>
            title="Sort"
            options={Object.values(SortBy)}
            selectedOption={filterSortBy as SortBy}
            onOptionSelect={setFilterSortBy}
            mapOptionToTitle={(option) => mapSortByToTitle[option]}
          />
          <ListOptions<Filter>
            title="Status"
            options={Object.values(Filter)}
            selectedOption={filterStatus}
            onOptionSelect={setFilterStatus}
            mapOptionToTitle={(option) => mapFilterToTitle[option]}
          />
          <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
        </Stack>
      </Stack>
      <Stack flex={1} ref={containerRef} overflow="hidden">
        {getContent()}
      </Stack>
    </Stack>
  );
}

export default Chains;
